import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import PublicLayout from '../components/PublicLayout'
import Button from '../components/Button'
import SignupCTA from '../components/SignupCTA'
import Badge from '../components/Badge'
import SampleDigest from '../components/SampleDigest'
import { TIERS } from '../tiers'
import {
  GRANTMAKERS, PUBLIC_CHARITY_GRANTMAKERS, FEDERAL_PROGRAMS,
  GRANTS_PAID_SHORT, FILING_YEARS, fmt,
} from '../corpus'
import {
  RadarIcon, FilterIcon, MailIcon, CheckIcon,
  ClockIcon, ShieldIcon, BoltIcon, UsersIcon,
} from '../components/Icons'

const STEPS = [
  {
    icon: RadarIcon,
    title: 'We read your mission',
    body: 'Tell us once who you serve, what you do and where. That mission is what we match against, so setup takes five minutes and needs no logins.',
  },
  {
    icon: FilterIcon,
    title: 'We find the funders',
    // Says "grantmakers", not "private foundations": about one in six of the
    // corpus files a 990 Schedule I rather than a 990-PF (PUBLIC_CHARITY_GRANTMAKERS
    // in corpus.js), and the old wording described the rest of our own data
    // while dropping the part hardest to find anywhere else.
    body: 'We rank the grantmakers whose own filings show they fund work like yours, private foundations and grantmaking public charities alike, and watch federal grants alongside. Conservative by design: quality over volume.',
  },
  {
    icon: MailIcon,
    title: 'You get a Tuesday digest',
    body: 'A short, ranked list of the foundations to approach and grants to apply for lands in your inbox every Tuesday morning, with a note on why each one matched, checked against the filing before you see it.',
  },
]

/**
 * The corpus, stated as numbers directly under the hero.
 *
 * This is the moat and until 2026-08-13 the site never once said how big it is.
 * Figures come from `corpus.js` rather than being typed here, so they cannot
 * drift away from the other five places that quote them.
 */
const FIGURES = [
  { n: fmt(GRANTMAKERS), label: 'grantmakers on record' },
  { n: GRANTS_PAID_SHORT, label: 'grants they actually paid' },
  { n: fmt(PUBLIC_CHARITY_GRANTMAKERS), label: 'grantmaking public charities' },
  { n: fmt(FEDERAL_PROGRAMS), label: 'federal programs tracked' },
]

/**
 * What exists behind the digest.
 *
 * "No login required to get value" is the FLOOR, not the ceiling (owner
 * direction, 2026-08-11). The homepage used to sell the absence of these
 * surfaces ("No logins, no searching") while three of them shipped, which is
 * the misreading CLAUDE.md warns has already argued against building things
 * worth building.
 */
const BEYOND = [
  {
    title: 'Search your matched foundations',
    body: 'Every grantmaker matched to your organization, filtered by state, focus area or typical grant size.',
  },
  {
    title: `Search all ${fmt(GRANTMAKERS)} grantmakers`,
    body: 'The whole corpus, ranked against your mission. Each one tells you why it is, or is not, in your digest. Included with Pro.',
  },
  {
    title: 'A full profile for any funder',
    body: 'Their filed application details and their real giving history, restated from the 990. If the filing left a field blank, we leave it blank.',
  },
  {
    title: 'Foundations that fund alongside yours',
    body: 'Starting from the funders already backing you, the ones that appear beside them in other filings.',
  },
]

/** The two things that come from reading the CLIENT's filing, not the funder's. */
const OWN_FILINGS = [
  {
    title: 'Which foundations have already funded you',
    body: 'Your filings are public, and reading them is how the digest knows who has backed you before. When we cannot identify your organization, it says so rather than guessing.',
  },
  {
    title: 'Matched on what you do, not what you typed',
    body: 'Your mission is compared against how funders describe the work they actually pay for, so a good match does not depend on picking the right keyword.',
  },
]

/**
 * The refusals, sold as a feature.
 *
 * Every line here is a real behaviour with a real implementation, not a
 * positioning statement: absent rows on blank fields (FunderProfile), the
 * four-outcome co-funder panel that never renders "no co-funders", and
 * `deadline_is_stale` relabelling a filed-but-passed cycle.
 */
const REFUSALS = [
  {
    title: 'Fill in a blank',
    body: 'If a filing left a field empty, the row is absent. Nothing is inferred to make a card look complete.',
  },
  {
    title: 'Guess when it cannot check',
    body: 'If we cannot identify your organization in the filings, you are told that. You are never told that nobody funds you.',
  },
  {
    title: 'Present a closed deadline as live',
    body: 'Deadlines are shown exactly as filed, and a cycle that has already passed is labelled as one.',
  },
]

const AUDIENCE = [
  {
    icon: UsersIcon,
    title: 'Development coordinators',
    body: 'You wear ten hats and grant research keeps slipping. Let it run itself.',
  },
  {
    icon: ClockIcon,
    title: 'Executive directors',
    // Budget range removed with the hero badge (owner decision 2026-08-13).
    // This was the second size mention in the top third of the page, and the
    // sentence lands harder without it.
    body: 'One grant you never heard about can cost more than a year of Praenum.',
  },
  {
    icon: BoltIcon,
    title: 'Freelance grant writers',
    // ⚠️ Said "with white-label digests" until 2026-08-13, which promised as
    // shipped a feature `tiers.js` marks "(coming soon)" two sections further
    // down the same page. Multi-client and white-label are both still unbuilt.
    body: 'Monitor opportunities for every client from one place.',
  },
]

// The old first item read "Built on public IRS 990 & federal grant data", which
// is true of every tool in the category and is said far better by the figures
// band. These two survivors move down beside the pricing teaser, where the
// cancel-anytime promise is doing conversion work rather than sitting in an
// icon strip.
const TRUST = [
  { icon: MailIcon, label: 'Reliable inbox delivery, never spam' },
  { icon: CheckIcon, label: 'Cancel anytime, no contracts' },
]

const FAQ_PREVIEW = [
  {
    q: 'Where do the leads come from?',
    a: `Public IRS filings, the record of who each grantmaker actually funds. That is ${fmt(GRANTMAKERS)} grantmakers, covering both private foundations and the ${fmt(PUBLIC_CHARITY_GRANTMAKERS)} public charities that make grants of their own. We rank the ones giving to organizations like yours, and watch federal grants from Grants.gov alongside. Foundations ship in every paid plan.`,
  },
  {
    q: 'Can I search the whole database myself?',
    a: `Yes. Every plan lets you search the grantmakers matched to your organization. Pro and Consultant add the rest of the corpus, all ${fmt(GRANTMAKERS)} of them, ranked against your mission, and each result will tell you why it is or is not in your weekly digest.`,
  },
  {
    q: 'How do you keep the matches relevant?',
    a: 'We score every foundation and grant against your mission and rank them conservatively, so you get a short list of strong fits instead of a flood of maybes. Thumbs-up or thumbs-down on any match and future digests get sharper.',
  },
]

export default function Home() {
  const location = useLocation()

  // Smooth-scroll to the hash target (e.g. /#how-it-works) when present.
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location])

  return (
    <PublicLayout
      title="Grant monitoring for small nonprofits"
      description={`Praenum reads your nonprofit's mission and emails a ranked shortlist of the foundations to approach and the federal grants worth your time, every Tuesday. Built on ${fmt(GRANTMAKERS)} grantmakers and the grants they actually paid, from public IRS filings.`}
    >
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-100 to-white">
        <div className="absolute inset-0 -z-10 opacity-60"
          style={{ background: 'radial-gradient(60% 50% at 50% 0%, rgba(99,102,241,0.18), transparent)' }} />
        <div className="max-w-6xl mx-auto px-4 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            {/* No budget range here, deliberately (owner decision 2026-08-13).
                A number in the slot above the H1 reads as an eligibility rule
                whatever verb precedes it, and softening "For" to "Built for"
                did not change that: a development director at a larger org sees
                a range they sit outside and stops reading.

                Budget was always a PROXY. What actually predicts fit is that
                grant research is nobody's full-time job, which is as true at
                $6M with a one-person development shop as it is at $300K. So the
                badge keeps its specificity and aims it at the situation instead
                of the size, and nobody is excluded by a number. Same framing as
                outreach variant B, "where grant research is somebody's fourth
                priority on a Tuesday".

                The segment itself is still stated, in the two places where it
                is context rather than a gate: the FAQ ("who it was designed
                around rather than a limit on who it serves") and About's
                problem paragraph. */}
            <Badge variant="indigo" className="mb-4">Built for the person who does grants alongside everything else</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-900 leading-tight">
              Find the foundations that fund organizations like yours.
            </h1>
            {/* Was "No logins, no searching, and the best value of any
                grant-monitoring tool." The first half contradicted three shipped
                search surfaces; the second was an unprovable comparative claim. */}
            <p className="mt-5 text-lg text-gray-600 max-w-xl">
              Praenum reads your mission and emails a short, ranked list of the
              foundations to approach, plus the federal grants worth your time, every
              Tuesday morning. You never have to log in to get it. When you do want to
              dig, all {fmt(GRANTMAKERS)} grantmakers are there to search.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <SignupCTA label="Start your free trial" size="lg" />
              <Link to="/pricing">
                <Button variant="secondary" size="lg">See pricing</Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Set up in under 5 minutes · No credit card to start · Cancel anytime
            </p>
          </div>

          <div className="lg:pl-6">
            <SampleDigest />
          </div>
        </div>
      </section>

      {/* The corpus, in figures. Directly under the hero because it is the
          single strongest thing on the page and the site never said it. */}
      <section className="max-w-6xl mx-auto px-4 pt-14">
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
          {FIGURES.map(f => (
            <div key={f.label} className="bg-white px-5 py-6">
              <dt className="sr-only">{f.label}</dt>
              <dd>
                <span className="block text-3xl font-extrabold tracking-tight text-brand-500 tabular-nums">
                  {f.n}
                </span>
                <span className="block mt-1.5 text-sm text-gray-600 leading-snug">
                  {f.label}
                </span>
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-sm text-gray-500">
          Built from public IRS filings for {FILING_YEARS}.
        </p>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 py-20 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-brand-900">How it works</h2>
          <p className="mt-3 text-gray-600">
            From a 5-minute setup to a curated inbox digest, fully automated.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-brand-100 text-brand-500 mb-5">
                <s.icon className="w-6 h-6" />
              </div>
              <span className="absolute top-6 right-6 text-sm font-semibold text-gray-500">
                0{i + 1}
              </span>
              <h3 className="font-semibold text-gray-900">{s.title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-900">Built for the people chasing the grants</h2>
            <p className="mt-3 text-gray-600">
              If grant monitoring is one task among twenty, Praenum takes it off your plate.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {AUDIENCE.map(a => (
              <div key={a.title} className="rounded-2xl bg-white border border-gray-200 p-7">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-brand-100 text-brand-500 mb-4">
                  <a.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900">{a.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beyond the digest: the surfaces the old copy sold the absence of. */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-brand-900">The email is the floor, not the ceiling</h2>
          <p className="mt-3 text-gray-600">
            You never have to log in. Everything behind the digest is waiting for the
            weeks when you do want to dig.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {BEYOND.map(b => (
            <div key={b.title} className="rounded-2xl border border-gray-200 bg-white p-7">
              <h3 className="font-semibold text-gray-900">{b.title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Built on the client's own filing, which is the part nothing else does. */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-900">Built on your filings, not only theirs</h2>
            <p className="mt-3 text-gray-600">
              Two things follow from reading your own 990 alongside the funders'.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {OWN_FILINGS.map(o => (
              <div key={o.title} className="rounded-2xl bg-white border border-gray-200 p-7">
                <h3 className="font-semibold text-gray-900">{o.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The refusals. Every line is a shipped behaviour, not a positioning claim. */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-brand-900">What Praenum will not do</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {REFUSALS.map(r => (
            <div key={r.title} className="rounded-2xl border border-gray-200 bg-white p-7">
              <h3 className="font-semibold text-gray-900">{r.title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-900">Simple, honest pricing</h2>
            <p className="mt-3 text-gray-600">
              Plans for a single nonprofit up to a consultant juggling many clients.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map(tier => (
              <div
                key={tier.key}
                className={`rounded-2xl border p-6 flex flex-col bg-white ${
                  tier.highlight ? 'border-brand-300 ring-2 ring-brand-100' : 'border-gray-200'
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="font-semibold text-gray-900">{tier.name}</h3>
                  {tier.highlight && <span className="text-xs font-medium text-brand-500">Popular</span>}
                </div>
                <p className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-sm text-gray-500">/mo</span>
                </p>
                <p className="text-sm text-gray-500 mt-2 flex-1">{tier.blurb}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col items-center gap-7">
            <Link to="/pricing">
              <Button variant="secondary" size="lg">Compare all plans</Button>
            </Link>
            {/* The two survivors of the old trust strip, re-homed here where the
                cancel-anytime promise sits beside the prices it qualifies. */}
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {TRUST.map(t => (
                <div key={t.label} className="flex items-center gap-2 text-sm text-gray-600">
                  <t.icon className="w-5 h-5 text-brand-500 flex-shrink-0" />
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="max-w-3xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-brand-900 text-center">Common questions</h2>
        <div className="mt-10 space-y-4">
          {FAQ_PREVIEW.map(item => (
            <div key={item.q} className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="font-semibold text-gray-900">{item.q}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/faq" className="text-sm font-medium text-brand-500 hover:underline">
            Read the full FAQ →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-900">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            The next grant you qualify for is already open.
          </h2>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Set up your profile in five minutes and let Praenum do the watching.
          </p>
          <div className="mt-8 flex justify-center">
            <SignupCTA label="Start your free trial" size="lg" />
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
